<form class="qe" [formGroup]="form" (ngSubmit)="submit()" novalidate>

  <!-- ═══════════ En-tête ═══════════ -->
  <header class="qe__header">
    <h2 class="qe__title">
      <span class="qe__title-icon"><i class="fa-solid fa-pen-to-square"></i></span>
      {{ (initialValue ? 'QUESTION_EDITOR.TITLE_EDIT' : 'QUESTION_EDITOR.TITLE_CREATE') | translate }}
    </h2>
    <p class="qe__subtitle">{{ 'QUESTION_EDITOR.SUBTITLE' | translate }}</p>
  </header>

  <!-- ═══════════ Sélecteur de type ═══════════ -->
  <fieldset class="qe__types">
    <legend class="qe__section-label">{{ 'QUESTION_EDITOR.TYPE' | translate }}</legend>

    <div class="qe__types-list">
      @for (option of questionTypes; track option.type) {
        <button
          type="button"
          class="qe__type"
          [class.qe__type--active]="questionType() === option.type"
          [attr.aria-pressed]="questionType() === option.type"
          (click)="selectType(option.type)">
          <i class="qe__type-icon" [class]="option.icon"></i>
          <span class="qe__type-label">{{ option.labelKey | translate }}</span>
        </button>
      }
    </div>
  </fieldset>

  <div class="qe__grid">

    <!-- ═══════════════════════════════════════════════════════
         COLONNE ÉDITION
         ═══════════════════════════════════════════════════════ -->
    <section class="qe__pane qe__pane--editor">

      <!-- Énoncé -->
      <div class="qe__field">
        <label class="qe__label" for="qe-title">
          {{ 'QUESTION_EDITOR.STATEMENT' | translate }}
          <span class="qe__required">*</span>
        </label>
        <app-rich-field>
          <textarea
            id="qe-title"
            class="form-control qe__control qe__control--statement"
            rows="2"
            formControlName="title"
            [placeholder]="'QUESTION_EDITOR.STATEMENT_PLACEHOLDER' | translate"></textarea>
        </app-rich-field>
        @if ((submitted() || form.controls.title.touched) &&
             (form.controls.title.hasError('required') || form.controls.title.hasError('blank'))) {
          <small class="qe__error">{{ 'QUESTION_EDITOR.ERROR_TITLE_REQUIRED' | translate }}</small>
        }
      </div>

      <!-- Description -->
      <div class="qe__field">
        <label class="qe__label" for="qe-description">
          {{ 'QUESTION_EDITOR.DESCRIPTION' | translate }}
          <span class="qe__optional">{{ 'QUESTION_EDITOR.OPTIONAL' | translate }}</span>
        </label>
        <app-rich-field>
          <textarea
            id="qe-description"
            class="form-control qe__control"
            rows="2"
            formControlName="description"
            [placeholder]="'QUESTION_EDITOR.DESCRIPTION_PLACEHOLDER' | translate"></textarea>
        </app-rich-field>
      </div>

      <!-- Descripteurs du référentiel — les quatre colonnes du modèle
           d'importation, saisies ici à la main. Le serveur en tire la
           compétence ciblée, ses niveaux et ses prérequis : c'est ce
           rattachement, et non ces libellés, qui rend le rapport de diagnostic
           calculable. -->
      @if (showReferential) {
        <div class="qe__field" [formGroup]="referentialForm">
          <label class="qe__label">
            {{ 'QUESTION_EDITOR.REFERENTIAL' | translate }}
          </label>
          <p class="qe__hint">{{ 'QUESTION_EDITOR.REFERENTIAL_HINT' | translate }}</p>

          <div class="qe__referential">
            <div class="qe__referential-cell">
              <label class="qe__referential-label" for="qe-targeted">
                {{ 'QUESTION_EDITOR.TARGETED' | translate }}
              </label>
              <input id="qe-targeted" type="text" class="qe__input"
                     formControlName="targeted"
                     [placeholder]="'QUESTION_EDITOR.TARGETED_PLACEHOLDER' | translate" />
            </div>

            <div class="qe__referential-cell">
              <label class="qe__referential-label" for="qe-skill">
                {{ 'QUESTION_EDITOR.SKILL' | translate }}
              </label>
              <input id="qe-skill" type="text" class="qe__input"
                     formControlName="skill"
                     [placeholder]="'QUESTION_EDITOR.SKILL_PLACEHOLDER' | translate" />
            </div>

            <div class="qe__referential-cell">
              <label class="qe__referential-label" for="qe-level">
                {{ 'QUESTION_EDITOR.LEVEL' | translate }}
              </label>
              <input id="qe-level" type="text" class="qe__input"
                     formControlName="level"
                     [placeholder]="'QUESTION_EDITOR.LEVEL_PLACEHOLDER' | translate" />
            </div>

            <div class="qe__referential-cell">
              <label class="qe__referential-label" for="qe-prerequisites">
                {{ 'QUESTION_EDITOR.PREREQUISITES' | translate }}
              </label>
              <input id="qe-prerequisites" type="text" class="qe__input"
                     formControlName="prerequisites"
                     [placeholder]="'QUESTION_EDITOR.PREREQUISITES_PLACEHOLDER' | translate" />
            </div>
          </div>
        </div>
      }

      <!-- Compétences évaluées. Masquées en mode diagnostique : on y décrit la
           compétence plutôt que de la choisir, et proposer les deux gestes pour
           un même rattachement laisse deviner lequel fait foi. -->
      @if (!diagnostic && competencies.length > 0) {
        <div class="qe__field">
          <label class="qe__label">
            {{ 'QUESTION_EDITOR.COMPETENCIES' | translate }}
            <span class="qe__optional">{{ 'QUESTION_EDITOR.OPTIONAL' | translate }}</span>
          </label>
          <p class="qe__hint">{{ 'QUESTION_EDITOR.COMPETENCIES_HINT' | translate }}</p>

          <div class="qe__competencies">
            @for (c of competencies; track c.id) {
              <label class="qe__competency" [class.qe__competency--on]="isCompetencySelected(c.id)">
                <input type="checkbox"
                       [checked]="isCompetencySelected(c.id)"
                       (change)="toggleCompetency(c.id)">
                <span class="qe__competency-body">
                  <span class="qe__competency-title">{{ c.title }}</span>
                  @if (c.domainTitle) {
                    <span class="qe__competency-domain">{{ c.domainTitle }}</span>
                  }
                  @if (c.skillDescriptor) {
                    <span class="qe__competency-skill">{{ c.skillDescriptor }}</span>
                  }
                  @if (c.gradeLevels.length > 0) {
                    <span class="qe__competency-levels">{{ c.gradeLevels.join(' · ') }}</span>
                  }
                </span>
              </label>
            }
          </div>
        </div>
      }

      <!-- Illustration de l'énoncé -->
      <div class="qe__field">
        <label class="qe__label" for="qe-image">
          {{ 'QUESTION_EDITOR.IMAGE' | translate }}
          <span class="qe__optional">{{ 'QUESTION_EDITOR.OPTIONAL' | translate }}</span>
        </label>

        @if (imageUrl()) {
          <div class="qe__image">
            <img [src]="imageUrl()" [alt]="'QUESTION_EDITOR.IMAGE_ALT' | translate">
            <button type="button" class="qe__image-remove"
                    [title]="'QUESTION_EDITOR.IMAGE_REMOVE' | translate"
                    (click)="removeImage()">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        } @else {
          <input
            id="qe-image"
            type="file"
            class="form-control qe__control"
            accept="image/png,image/jpeg,image/gif,image/webp"
            [disabled]="imageUploading()"
            (change)="onImageSelected($event)">
          <small class="qe__hint">{{ 'QUESTION_EDITOR.IMAGE_HINT' | translate }}</small>
        }

        @if (imageUploading()) {
          <small class="qe__hint">
            <i class="fa-solid fa-spinner fa-spin me-1"></i>
            {{ 'QUESTION_EDITOR.IMAGE_UPLOADING' | translate }}
          </small>
        }

        @if (imageError(); as err) {
          <small class="qe__error">{{ err | translate }}</small>
        }
      </div>

      <!-- Points / difficulté / temps -->
      <div class="qe__row">
        <div class="qe__field qe__field--third">
          <label class="qe__label" for="qe-points">
            {{ 'QUESTION_EDITOR.POINTS' | translate }}
            <span class="qe__required">*</span>
          </label>
          <input
            id="qe-points"
            class="form-control qe__control"
            type="number"
            step="any"
            min="0"
            formControlName="points" />
          @if ((submitted() || form.controls.points.touched) &&
               (form.controls.points.hasError('required') || form.controls.points.hasError('positive'))) {
            <small class="qe__error">{{ 'QUESTION_EDITOR.ERROR_POINTS_POSITIVE' | translate }}</small>
          }
        </div>

        <div class="qe__field qe__field--third">
          <label class="qe__label" for="qe-difficulty">
            {{ 'QUESTION_EDITOR.DIFFICULTY' | translate }}
          </label>
          <select
            id="qe-difficulty"
            class="form-select qe__control"
            formControlName="difficultyLevel">
            @for (level of difficulties; track level.value) {
              <option [value]="level.value">{{ level.labelKey | translate }}</option>
            }
          </select>
        </div>

        <div class="qe__field qe__field--third">
          <label class="qe__label" for="qe-time-limit">
            {{ 'QUESTION_EDITOR.TIME_LIMIT' | translate }}
            <span class="qe__optional">{{ 'QUESTION_EDITOR.OPTIONAL' | translate }}</span>
          </label>
          <input
            id="qe-time-limit"
            class="form-control qe__control"
            type="number"
            min="1"
            formControlName="timeLimitSeconds"
            [placeholder]="'QUESTION_EDITOR.TIME_LIMIT_PLACEHOLDER' | translate" />
          @if ((submitted() || form.controls.timeLimitSeconds.touched) &&
               form.controls.timeLimitSeconds.hasError('positive')) {
            <small class="qe__error">{{ 'QUESTION_EDITOR.ERROR_TIME_LIMIT_POSITIVE' | translate }}</small>
          }
        </div>
      </div>

      <!-- ═══════════ Bloc de réponses polymorphe ═══════════ -->
      <div class="qe__answers">
        <div class="qe__answers-head">
          <span class="qe__section-label">{{ 'QUESTION_EDITOR.ANSWERS' | translate }}</span>
        </div>

        @switch (questionType()) {
          @case ('short_text') {
            <app-text-answers
              [group]="textForm"
              [minAnswers]="minOptions()"
              [maxAnswers]="maxAcceptedAnswers"
              (addAnswer)="onAddAcceptedAnswer()"
              (removeAnswer)="onRemoveAcceptedAnswer($event)" />
          }
          @case ('numeric') {
            <app-numeric-answer [group]="numericForm" />
          }
          @default {
            <app-choice-answers
              [group]="choiceForm"
              [mode]="choiceMode()"
              [minOptions]="minOptions()"
              [maxOptions]="maxOptions"
              (addOption)="onAddOption()"
              (removeOption)="onRemoveOption($event)"
              (correctSelected)="onCorrectSelected($event)" />
          }
        }

        @if (submitted() && answerErrors().length > 0) {
          <ul class="qe__errors">
            @for (error of answerErrors(); track error.key) {
              <li class="qe__error">
                <i class="fa-solid fa-triangle-exclamation"></i>
                {{ error.key | translate: error.params }}
              </li>
            }
          </ul>
        }
      </div>

      <!-- Explication -->
      <div class="qe__field">
        <label class="qe__label" for="qe-explanation">
          {{ 'QUESTION_EDITOR.EXPLANATION' | translate }}
          <span class="qe__optional">{{ 'QUESTION_EDITOR.OPTIONAL' | translate }}</span>
        </label>
        <app-rich-field>
          <textarea
            id="qe-explanation"
            class="form-control qe__control"
            rows="3"
            formControlName="explanation"
            [placeholder]="'QUESTION_EDITOR.EXPLANATION_PLACEHOLDER' | translate"></textarea>
        </app-rich-field>
      </div>

      <!-- Indice -->
      <div class="qe__field">
        <label class="qe__label" for="qe-hints">
          {{ 'QUESTION_EDITOR.HINT' | translate }}
          <span class="qe__optional">{{ 'QUESTION_EDITOR.OPTIONAL' | translate }}</span>
        </label>
        <input
          id="qe-hints"
          class="form-control qe__control"
          type="text"
          formControlName="hints"
          [placeholder]="'QUESTION_EDITOR.HINT_PLACEHOLDER' | translate" />
      </div>

      <!-- Étiquettes -->
      <div class="qe__field">
        <label class="qe__label" for="qe-tag-draft">
          {{ 'QUESTION_EDITOR.TAGS' | translate }}
          <span class="qe__optional">{{ 'QUESTION_EDITOR.OPTIONAL' | translate }}</span>
        </label>

        <div class="qe__tags-input">
          <input
            id="qe-tag-draft"
            class="form-control qe__control"
            type="text"
            [formControl]="tagDraft"
            [placeholder]="'QUESTION_EDITOR.TAG_PLACEHOLDER' | translate"
            (keydown.enter)="$event.preventDefault(); addTag()" />
          <button class="qe__tag-add" type="button" (click)="addTag()">
            <i class="fa-solid fa-plus"></i>
            {{ 'QUESTION_EDITOR.ADD_TAG' | translate }}
          </button>
        </div>

        @if (tags.length > 0) {
          <ul class="qe__tags">
            @for (tag of tags.controls; track tag; let i = $index) {
              <li class="qe__tag">
                <span>{{ tag.value }}</span>
                <button
                  type="button"
                  class="qe__tag-remove"
                  [attr.aria-label]="'QUESTION_EDITOR.REMOVE_TAG' | translate"
                  (click)="removeTag(i)">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </li>
            }
          </ul>
        }
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════
         COLONNE APERÇU ÉLÈVE
         ═══════════════════════════════════════════════════════ -->
    <aside class="qe__pane qe__pane--preview" aria-live="polite">
      <div class="qe__preview-card">

        <div class="qe__preview-head">
          <span class="qe__preview-badge">
            <i class="fa-regular fa-eye"></i>
            {{ 'QUESTION_EDITOR.PREVIEW' | translate }}
          </span>
          <span class="qe__preview-type">
            {{ ('QUESTION_EDITOR.TYPE_' + preview().type.toUpperCase()) | translate }}
          </span>
        </div>

        <div class="qe__preview-meta">
          <span class="qe__chip">
            <i class="fa-solid fa-star"></i>
            {{ 'QUESTION_EDITOR.PREVIEW_POINTS' | translate: { points: preview().points ?? 0 } }}
          </span>
          <span class="qe__chip">
            <i class="fa-solid fa-gauge-high"></i>
            {{ ('QUESTION_EDITOR.DIFFICULTY_' + preview().difficultyLevel.toUpperCase()) | translate }}
          </span>
          @if (preview().timeLimitSeconds) {
            <span class="qe__chip">
              <i class="fa-regular fa-clock"></i>
              {{ 'QUESTION_EDITOR.PREVIEW_TIME' | translate: { seconds: preview().timeLimitSeconds } }}
            </span>
          }
        </div>

        <p class="qe__preview-statement" [class.qe__preview-statement--empty]="!preview().title">
          @if (preview().title) {
            <app-math-text [text]="preview().title"></app-math-text>
          } @else {
            {{ 'QUESTION_EDITOR.PREVIEW_EMPTY_STATEMENT' | translate }}
          }
        </p>

        @if (preview().description) {
          <p class="qe__preview-description"><app-math-text [text]="preview().description"></app-math-text></p>
        }

        <!-- Rendu des réponses tel que l'élève les verra -->
        <div class="qe__preview-answers">
          @switch (preview().type) {
            @case ('short_text') {
              <input
                id="qe-preview-text"
                class="form-control qe__preview-input"
                type="text"
                disabled
                [attr.aria-label]="'QUESTION_EDITOR.PREVIEW_TEXT_PLACEHOLDER' | translate"
                [placeholder]="'QUESTION_EDITOR.PREVIEW_TEXT_PLACEHOLDER' | translate" />
            }
            @case ('numeric') {
              <input
                id="qe-preview-numeric"
                class="form-control qe__preview-input"
                type="number"
                disabled
                [attr.aria-label]="'QUESTION_EDITOR.PREVIEW_NUMERIC_PLACEHOLDER' | translate"
                [placeholder]="'QUESTION_EDITOR.PREVIEW_NUMERIC_PLACEHOLDER' | translate" />
            }
            @default {
              @for (choice of preview().choices; track choice.letter) {
                <label class="qe__preview-choice">
                  <input
                    class="form-check-input"
                    [type]="preview().type === 'multiple_choice' ? 'checkbox' : 'radio'"
                    disabled />
                  <span class="qe__preview-choice-letter">{{ choice.letter }}</span>
                  <span class="qe__preview-choice-label">
                    @if (choice.labelKey) {
                      {{ choice.labelKey | translate }}
                    } @else {
                      @if (choice.label) {
                        <app-math-text [text]="choice.label"></app-math-text>
                      } @else {
                        {{ 'QUESTION_EDITOR.PREVIEW_EMPTY_OPTION' | translate }}
                      }
                    }
                  </span>
                </label>
              }
            }
          }
        </div>

        @if (preview().hints) {
          <p class="qe__preview-hint">
            <i class="fa-regular fa-lightbulb"></i>
            {{ preview().hints }}
          </p>
        }

        <!-- Solution : visible du professeur uniquement -->
        <button class="qe__preview-toggle" type="button" (click)="toggleSolution()">
          <i class="fa-solid" [class.fa-chevron-down]="!solutionVisible()"
             [class.fa-chevron-up]="solutionVisible()"></i>
          {{ (solutionVisible() ? 'QUESTION_EDITOR.HIDE_SOLUTION' : 'QUESTION_EDITOR.SHOW_SOLUTION') | translate }}
        </button>

        @if (solutionVisible()) {
          <div class="qe__preview-solution">
            @switch (preview().type) {
              @case ('short_text') {
                @if (preview().acceptedAnswers.length > 0) {
                  <ul class="qe__solution-list">
                    @for (answer of preview().acceptedAnswers; track answer) {
                      <li>{{ answer }}</li>
                    }
                  </ul>
                  @if (preview().matchOptions; as options) {
                    <p class="qe__solution-note">
                      {{ (options.caseSensitive
                            ? 'QUESTION_EDITOR.SOLUTION_CASE_SENSITIVE'
                            : 'QUESTION_EDITOR.SOLUTION_CASE_INSENSITIVE') | translate }}
                      @if (options.trimWhitespace) {
                        · {{ 'QUESTION_EDITOR.SOLUTION_TRIM' | translate }}
                      }
                      @if (options.normalizeArabic) {
                        · {{ 'QUESTION_EDITOR.SOLUTION_NORMALIZE_ARABIC' | translate }}
                      }
                    </p>
                  }
                } @else {
                  <p class="qe__solution-note">{{ 'QUESTION_EDITOR.SOLUTION_EMPTY' | translate }}</p>
                }
              }
              @case ('numeric') {
                @if (preview().numericValue !== null) {
                  <p class="qe__solution-note">
                    {{ 'QUESTION_EDITOR.RANGE_ACCEPTED' | translate:
                       { min: preview().numericMin, max: preview().numericMax } }}
                  </p>
                } @else {
                  <p class="qe__solution-note">{{ 'QUESTION_EDITOR.SOLUTION_EMPTY' | translate }}</p>
                }
              }
              @default {
                <ul class="qe__solution-list">
                  @for (choice of preview().choices; track choice.letter) {
                    @if (choice.isCorrect) {
                      <li>
                        {{ choice.letter }} —
                        @if (choice.labelKey) {
                          {{ choice.labelKey | translate }}
                        } @else {
                          @if (choice.label) {
                            <app-math-text [text]="choice.label"></app-math-text>
                          } @else {
                            {{ 'QUESTION_EDITOR.PREVIEW_EMPTY_OPTION' | translate }}
                          }
                        }
                      </li>
                    }
                  }
                </ul>
              }
            }

            @if (preview().explanation) {
              <p class="qe__solution-explanation"><app-math-text [text]="preview().explanation"></app-math-text></p>
            }
          </div>
        }

        @if (preview().tags.length > 0) {
          <ul class="qe__preview-tags">
            @for (tag of preview().tags; track tag) {
              <li class="qe__preview-tag">#{{ tag }}</li>
            }
          </ul>
        }
      </div>
    </aside>
  </div>

  <!-- ═══════════ Pied de page ═══════════ -->
  <footer class="qe__footer">
    @if (submitted() && commonErrors().length > 0) {
      <ul class="qe__errors qe__errors--footer">
        @for (error of commonErrors(); track error.key) {
          <li class="qe__error">
            <i class="fa-solid fa-triangle-exclamation"></i>
            {{ error.key | translate: error.params }}
          </li>
        }
      </ul>
    }

    <div class="qe__actions">
      <button class="qe__btn qe__btn--ghost" type="button" (click)="onCancel()">
        {{ 'QUESTION_EDITOR.CANCEL' | translate }}
      </button>
      <button class="qe__btn qe__btn--primary" type="submit" [disabled]="submitted() && !isValid()">
        <i class="fa-solid fa-floppy-disk"></i>
        {{ 'QUESTION_EDITOR.SAVE' | translate }}
      </button>
    </div>
  </footer>
</form>
